/**
 * useModelRegistry — the data behind the model-defaults strip, wired to QUILL's
 * model control plane rather than LifeScribe's.
 *
 * The LifeScribe original (components/ai/styles/useModelRegistry.ts) reads
 * `/api/admin/ai-styles/xai-models` and writes a set of GLOBAL slots. Quill's
 * equivalents already exist and are already proxied to the browser, so this hook
 * adds no backend route — it calls the endpoints the Quill AI Models and
 * Pipeline Models pages already use, through the shared typed client.
 *
 * ── WHICH QUILL SETTING IS THE "WRITING MODEL" ────────────────────────────
 *
 * NOT `quill_model_defaults.text`. That global slot still exists and the AI
 * Models page still edits it, but since migration 528 nothing resolves through
 * it: `ModelResolver.resolvePipeline()` goes straight to the pipeline's own
 * active model, `ModelResolver.resolve(slot)` has no remaining production call
 * site in quill-engine, and quill-prosody's `pipeline_model.py` says in as many
 * words that it "does NOT consult quill_model_defaults". Binding the strip to it
 * would give an admin a control that writes an audit row and changes nothing.
 *
 * So the writing slot is bound to the `conversation_main` PIPELINE's active
 * model — the model Quill actually runs — through
 * `PUT /pipeline-models/conversation_main` and
 * `POST /pipeline-models/conversation_main/revert`.
 *
 * One thing this control does NOT do, and the strip says so on screen: it does
 * not change how the styles on this page read. Quill does not write chapter
 * prose — LifeScribe's transformer does, resolving each style by its
 * `ls_style_key` (see quill-engine `authorsService.js`, "THE INVARIANT").
 *
 * Mutations THROW (QuillApiError) rather than notifying, so the component owns
 * the production-confirmation flow and the copy.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ModelCatalogResponse,
  ModelCatalogRow,
  ModelRefreshResponse,
  PipelineModel,
  quillApi,
} from '../../../services/quillAdminService';
import { filterCatalogForPipeline } from '../pipeline-models/pipelineCatalog';

/** The pipeline whose active model composes Quill's prose. */
export const WRITING_PIPELINE_KEY = 'conversation_main';

/**
 * Source vocabulary, kept in the original's shape. For a pipeline-backed slot:
 *   db   — an admin chose this model (quill_pipeline_models.updated_by is set),
 *          so there is a prior value in the change log and Undo can work.
 *   seed — still on the value migration 523/528 seeded; nothing to revert to.
 *   none — the pipeline row could not be read at all.
 */
export type ModelSource = 'db' | 'seed' | 'none';

export interface EffectiveModel {
  value: string | null;
  provider: string | null;
  source: ModelSource;
  updatedBy?: string | null;
  updatedAt?: string | null;
  /** True when the active model equals the pipeline's default. */
  onDefault?: boolean;
}

const NO_MODEL: EffectiveModel = { value: null, provider: null, source: 'none' };

/**
 * Compact cost for a dropdown row — the LifeScribe strip's `$0.003/1M` shape,
 * built from Quill's `pricing_amount` / `pricing_unit` columns. Empty string when
 * the catalogue has no price for that model (never a fabricated zero).
 */
export function formatCost(row: ModelCatalogRow): string {
  if (row.pricing_amount === null || row.pricing_amount === undefined) return '';
  const unit = row.pricing_unit || '';
  const short = unit.includes('1M')
    ? '1M'
    : unit.includes('image')
      ? 'img'
      : unit.includes('second')
        ? 's'
        : unit;
  const amount =
    row.pricing_amount < 1
      ? `$${row.pricing_amount.toFixed(4)}`
      : `$${row.pricing_amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  return short ? `${amount}/${short}` : amount;
}

export interface UseModelRegistryResult {
  models: ModelCatalogRow[];
  writingPipeline: PipelineModel | null;
  loading: boolean;
  updating: boolean;
  error: string | null;
  lastSyncedAt: string | null;
  stale: boolean;
  syncAgeHours: number | null;
  reload: () => Promise<void>;
  refreshModels: () => Promise<ModelRefreshResponse>;
  /** PUT the pipeline's active model. `modelId` must be a catalogue row id. */
  updateWritingModel: (modelId: string, reason?: string) => Promise<void>;
  /** POST revert — restores the previous model recorded in the change log. */
  revertWritingModel: () => Promise<void>;
  /** Catalogue rows this pipeline may point at, sorted by model id. */
  writingModels: () => ModelCatalogRow[];
  effectiveWritingModel: () => EffectiveModel;
}

export function useModelRegistry(): UseModelRegistryResult {
  const [models, setModels] = useState<ModelCatalogRow[]>([]);
  const [writingPipeline, setWritingPipeline] = useState<PipelineModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [syncAgeHours, setSyncAgeHours] = useState<number | null>(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const applyCatalog = useCallback((resp: ModelCatalogResponse) => {
    setModels(Array.isArray(resp?.data) ? resp.data : []);
    setLastSyncedAt(resp?.last_synced_at ?? null);
    setStale(Boolean(resp?.stale));
    setSyncAgeHours(typeof resp?.sync_age_hours === 'number' ? resp.sync_age_hours : null);
  }, []);

  /**
   * Catalogue and pipelines load independently — a failing catalogue must not
   * hide the model that IS set, and vice versa. Matches the AI Models page.
   */
  const reload = useCallback(async () => {
    setLoading(true);
    const [available, pipelines] = await Promise.allSettled([
      quillApi.models.getAvailable(),
      quillApi.pipelineModels.list(),
    ]);
    if (!mountedRef.current) return;

    if (available.status === 'fulfilled') applyCatalog(available.value);
    if (pipelines.status === 'fulfilled') {
      const rows = Array.isArray(pipelines.value) ? pipelines.value : [];
      setWritingPipeline(rows.find((p) => p.pipeline_key === WRITING_PIPELINE_KEY) ?? null);
    }

    const failure = [available, pipelines].find((r) => r.status === 'rejected') as
      | PromiseRejectedResult
      | undefined;
    setError(
      failure
        ? failure.reason instanceof Error
          ? failure.reason.message
          : 'Failed to load the Quill model registry.'
        : null
    );
    setLoading(false);
  }, [applyCatalog]);

  useEffect(() => {
    reload();
  }, [reload]);

  const refreshModels = useCallback(async () => {
    setUpdating(true);
    try {
      const resp = await quillApi.models.refresh();
      if (mountedRef.current) {
        applyCatalog(resp);
        setError(null);
      }
      return resp;
    } finally {
      if (mountedRef.current) setUpdating(false);
    }
  }, [applyCatalog]);

  const updateWritingModel = useCallback(
    async (modelId: string, reason?: string) => {
      const row = models.find((m) => m.model_id === modelId);
      setUpdating(true);
      try {
        await quillApi.pipelineModels.setActive(WRITING_PIPELINE_KEY, {
          provider: row?.provider || 'xai',
          model_id: modelId,
          reason,
        });
        await reload();
      } finally {
        if (mountedRef.current) setUpdating(false);
      }
    },
    [models, reload]
  );

  const revertWritingModel = useCallback(async () => {
    setUpdating(true);
    try {
      await quillApi.pipelineModels.revert(WRITING_PIPELINE_KEY);
      await reload();
    } finally {
      if (mountedRef.current) setUpdating(false);
    }
  }, [reload]);

  /**
   * The writing slot's options, narrowed by exactly the rule the backend's PUT
   * enforces for this pipeline's category — so the strip can never offer a model
   * the write would reject.
   */
  const writingModels = useCallback(() => {
    if (!writingPipeline) return [];
    return filterCatalogForPipeline(
      models,
      writingPipeline.category,
      writingPipeline.pipeline_key
    ).sort((a, b) => a.model_id.localeCompare(b.model_id));
  }, [models, writingPipeline]);

  const effectiveWritingModel = useCallback((): EffectiveModel => {
    if (!writingPipeline?.active?.model_id) return NO_MODEL;
    return {
      value: writingPipeline.active.model_id,
      provider: writingPipeline.active.provider ?? null,
      source: writingPipeline.updated_by ? 'db' : 'seed',
      updatedBy: writingPipeline.updated_by,
      updatedAt: writingPipeline.updated_at,
      onDefault: writingPipeline.is_on_default,
    };
  }, [writingPipeline]);

  return {
    models,
    writingPipeline,
    loading,
    updating,
    error,
    lastSyncedAt,
    stale,
    syncAgeHours,
    reload,
    refreshModels,
    updateWritingModel,
    revertWritingModel,
    writingModels,
    effectiveWritingModel,
  };
}

export default useModelRegistry;
